// Live DSL editor. Bidirectional with the store: typing parses and replaces
// state on success; state changes update the textarea (when focused
// elsewhere). Errors appear inline.

import { effect } from '@/core/signals';
import { state, replaceState } from '@/core/state';
import { parseDsl, serializeDsl } from '@/core/dsl';
import { migrate } from '@/core/migrate';
import { el, on } from '@/lib/dom';

export function openDslEditor(): void {
  const dim = el('div', { class: 'modal-backdrop' });
  const dialog = el('div', { class: 'modal modal-dsl', role: 'dialog', ariaLabel: 'DSL editor' });

  const head = el('div', { class: 'modal-head' }, [
    el('div', { class: 'modal-title' }, ['DSL']),
    el('div', { class: 'modal-subtitle' }, ['Tweetable, sharable, lossless. Edits apply live.']),
  ]);

  const textarea = el('textarea', {
    class: 'dsl-text mono',
    value: serializeDsl(state.peek()),
    ariaLabel: 'DSL source',
  }) as HTMLTextAreaElement;
  textarea.spellcheck = false;

  const errorBox = el('div', { class: 'dsl-error mono' });

  const actions = el('div', { class: 'modal-actions' }, [
    actionBtn('Copy DSL', async () => {
      try { await navigator.clipboard.writeText(textarea.value); flash(actions, 'Copied'); } catch { /* noop */ }
    }),
    actionBtn('Close', () => close()),
  ]);

  dialog.append(head, textarea, errorBox, actions);
  dim.appendChild(dialog);
  document.body.appendChild(dim);

  let typing = false;
  on(textarea, 'input', () => {
    typing = true;
    const parsed = parseDsl(textarea.value);
    if (!parsed.ok) {
      errorBox.textContent = parsed.line ? `line ${parsed.line}: ${parsed.error}` : parsed.error;
      errorBox.classList.add('show');
      return;
    }
    errorBox.classList.remove('show');
    errorBox.textContent = '';
    const migrated = migrate(parsed.value);
    if (!migrated) {
      errorBox.textContent = 'unsupported version';
      errorBox.classList.add('show');
      return;
    }
    replaceState(
      {
        resolution: migrated.resolution,
        layers: migrated.layers,
        selectedLayerId: migrated.layers[0]?.id ?? null,
        soloLayerId: null,
        time: 0,
        paused: true,
        fps: 8,
      },
      true,
    );
    queueMicrotask(() => { typing = false; });
  });

  // External state changes refresh the textarea (only when not typing).
  const dispose = effect(() => {
    const s = state.get();
    if (typing) return;
    const dsl = serializeDsl(s);
    if (textarea.value !== dsl) textarea.value = dsl;
  });

  function close(): void {
    dispose();
    dim.remove();
    offKey();
    offClick();
  }

  const offKey = on(window, 'keydown', (ev) => {
    if (ev.key === 'Escape') close();
  });
  const offClick = on(dim, 'click', (ev) => {
    if (ev.target === dim) close();
  });

  textarea.focus();
}

function actionBtn(label: string, click: () => void): HTMLElement {
  const b = el('button', { class: 'btn modal-btn' }, [label]);
  on(b, 'click', click);
  return b;
}

function flash(host: HTMLElement, msg: string): void {
  const tag = el('span', { class: 'modal-flash mono' }, [msg]);
  host.appendChild(tag);
  setTimeout(() => tag.remove(), 1200);
}
